import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { Typography, FormControlLabel } from '@material-ui/core';
import { Button, Switch, AlertBanner, Loader } from '@saviynt/design-system';
import {
  saveConfig,
  saveAiAgentConfig,
  saveDisconnectedDiscoveryConfig,
  fetchSourceStatus,
  fetchUserAttributes,
} from '../../redux/migrationThunks';
import {
  TITLES,
  DESCRIPTIONS,
  UI_LABELS,
  BUTTON_LABELS,
  SUCCESS_MESSAGES,
  ERROR_MESSAGES,
} from '../../constants/stringConstants';

function GlobalConfiguration({ classes, sourceID }) {
  const dispatch = useDispatch();
  const { sourceStatus } = useSelector((state) => state.migration ?? {});
  const [enabled, setEnabled] = useState(sourceStatus?.dataMigrationEnabled ?? false);
  const [saving, setSaving] = useState(false);
  const [banner, setBanner] = useState({ visible: false, type: 'Success', message: '' });
  const [aiAgentEnabled, setAiAgentEnabled] = useState(
    sourceStatus?.aiAgentServiceEnabled ?? false
  );
  const [savingAiAgent, setSavingAiAgent] = useState(false);
  const [disconnectedDiscovery, setDisconnectedDiscovery] = useState(
    sourceStatus?.disconnectedDiscovery ?? false
  );
  const [savingDisconnectedDiscovery, setSavingDisconnectedDiscovery] = useState(false);

  const handleSave = () => {
    setSaving(true);
    dispatch(saveConfig({ configValue: String(enabled) }))
      .unwrap()
      .then(() => {
        if (sourceID) dispatch(fetchSourceStatus(sourceID));
        setBanner({ visible: true, type: 'Success', message: SUCCESS_MESSAGES.CONFIG_SAVED });
      })
      .catch((err) => {
        setBanner({
          visible: true,
          type: 'Critical',
          message: err?.message || ERROR_MESSAGES.FAILED_SAVE_CONFIG,
        });
      })
      .finally(() => setSaving(false));
  };

  const handleSaveAiAgent = () => {
    setSavingAiAgent(true);
    dispatch(saveAiAgentConfig({ configValue: String(aiAgentEnabled) }))
      .unwrap()
      .then(() => {
        if (sourceID) dispatch(fetchSourceStatus(sourceID));
        setBanner({
          visible: true,
          type: 'Success',
          message: SUCCESS_MESSAGES.AI_AGENT_CONFIG_SAVED,
        });
      })
      .catch((err) => {
        setBanner({
          visible: true,
          type: 'Critical',
          message: err?.message || ERROR_MESSAGES.FAILED_SAVE_AI_AGENT_CONFIG,
        });
      })
      .finally(() => setSavingAiAgent(false));
  };

  const handleSaveDisconnectedDiscovery = () => {
    setSavingDisconnectedDiscovery(true);
    dispatch(saveDisconnectedDiscoveryConfig({ configValue: String(disconnectedDiscovery) }))
      .unwrap()
      .then(() => {
        if (sourceID) {
          dispatch(fetchSourceStatus(sourceID));
          dispatch(fetchUserAttributes(sourceID));
        }
        setBanner({
          visible: true,
          type: 'Success',
          message: SUCCESS_MESSAGES.DISCONNECTED_DISCOVERY_CONFIG_SAVED,
        });
      })
      .catch((err) => {
        setBanner({
          visible: true,
          type: 'Critical',
          message: err?.message || ERROR_MESSAGES.FAILED_SAVE_DISCONNECTED_DISCOVERY_CONFIG,
        });
      })
      .finally(() => setSavingDisconnectedDiscovery(false));
  };

  return (
    <div className={classes.globalConfigWrapper}>
      {banner.visible && (
        <AlertBanner
          colorTheme={banner.type}
          title={banner.type === 'Success' ? 'Success' : 'Error'}
          description={banner.message}
          isVisible={banner.visible}
          isFloating
          position={{ type: 'fixed', top: '16px', right: '16px' }}
          onCancel={() => setBanner((prev) => ({ ...prev, visible: false }))}
          shouldAutoDismiss
          autoDismissDelay={5000}
          isCloseButtonVisible
          className={classes.alertBanner}
        />
      )}
      {saving && (
        <div className={classes.saveOverlay}>
          <Loader kind="loop" size="large" format="inline" color="brand" />
        </div>
      )}
      {savingAiAgent && (
        <div className={classes.saveOverlay}>
          <Loader kind="loop" size="large" format="inline" color="brand" />
        </div>
      )}
      {savingDisconnectedDiscovery && (
        <div className={classes.saveOverlay}>
          <Loader kind="loop" size="large" format="inline" color="brand" />
        </div>
      )}
      <Typography className={classes.configSectionTitle}>
        {TITLES.DATA_MIGRATION_SETTINGS}
      </Typography>
      <Typography className={classes.configSectionDesc}>
        {DESCRIPTIONS.DATA_MIGRATION_SETTINGS}
      </Typography>
      <div className={classes.configRows}>
        <div className={classes.configRow}>
          <div className={classes.configRowLabel}>
            <Typography className={classes.label}>{UI_LABELS.ENABLE_DATA_MIGRATION}</Typography>
            <Typography className={classes.configRowHint}>
              {DESCRIPTIONS.ENABLE_DATA_MIGRATION}
            </Typography>
          </div>
          <div className={classes.configRowInput}>
            <FormControlLabel
              control={
                <Switch
                  isChecked={enabled}
                  onChange={() => setEnabled(!enabled)}
                  ariaLabel="Toggle option for data migration tab"
                />
              }
            />
          </div>
          <Button kind="filled" size="medium" onClick={handleSave}>
            {BUTTON_LABELS.SAVE}
          </Button>
        </div>
      </div>
      <hr className={classes.configSectionDivider} />
      <Typography className={classes.configSectionTitle}>{TITLES.AI_AGENT_SERVICE}</Typography>
      <Typography className={classes.configSectionDesc}>{DESCRIPTIONS.AI_AGENT_SERVICE}</Typography>
      <div className={classes.configRows}>
        <div className={classes.configRow}>
          <div className={classes.configRowLabel}>
            <Typography className={classes.label}>{UI_LABELS.ENABLE_AI_AGENT_SERVICE}</Typography>
            <Typography className={classes.configRowHint}>
              {DESCRIPTIONS.ENABLE_AI_AGENT}
            </Typography>
          </div>
          <div className={classes.configRowInput}>
            <FormControlLabel
              control={
                <Switch
                  isChecked={aiAgentEnabled}
                  onChange={() => setAiAgentEnabled(!aiAgentEnabled)}
                  ariaLabel="Toggle option for AI agent service"
                />
              }
            />
          </div>
          <Button kind="filled" size="medium" onClick={handleSaveAiAgent}>
            {BUTTON_LABELS.SAVE}
          </Button>
        </div>
      </div>
      <hr className={classes.configSectionDivider} />
      <Typography className={classes.configSectionTitle}>
        {TITLES.DISCONNECTED_DISCOVERY} Mode
      </Typography>
      <Typography className={classes.configSectionDesc}>
        {DESCRIPTIONS.DISCONNECTED_DISCOVERY}
      </Typography>
      <div className={classes.configRows}>
        <div className={classes.configRow}>
          <div className={classes.configRowLabel}>
            <Typography className={classes.label}>
              {UI_LABELS.ENABLE_DISCONNECTED_DISCOVERY}
            </Typography>
            <Typography className={classes.configRowHint}>
              {DESCRIPTIONS.ENABLE_DISCONNECTED_DISCOVERY}
            </Typography>
          </div>
          <div className={classes.configRowInput}>
            <FormControlLabel
              control={
                <Switch
                  isChecked={disconnectedDiscovery}
                  onChange={() => setDisconnectedDiscovery(!disconnectedDiscovery)}
                  ariaLabel="Toggle option for disconnected discovery"
                />
              }
            />
          </div>
          <Button kind="filled" size="medium" onClick={handleSaveDisconnectedDiscovery}>
            {BUTTON_LABELS.SAVE}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default GlobalConfiguration;

GlobalConfiguration.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  classes: PropTypes.object.isRequired,
  sourceID: PropTypes.string,
};

GlobalConfiguration.defaultProps = {
  sourceID: null,
};
